import { ParcelTypeEnums, SameOrderModel } from "Data/order";
import { omitBy, isNil, omit, isEmpty, first } from "lodash";

const constructParcelModel = (data) => {
  if (!data) return {};

  const { height, width, length, weight, goods, remarks } = data || {};

  const dimension = omitBy(
    {
      height,
      width,
      length,
    },
    isNil,
  );

  const newGoods = goods.map((g) =>
    omitBy(
      {
        typeOfGood: g?.typeOfGood,
        goodsValue: g?.goodsValue,
        trackingNumber: g?.trackingNumber,
        name: g?.name,
      },
      isEmpty,
    ),
  );

  return omitBy(
    {
      parcels: newGoods,
      weight,
      dimension,
      remarks: remarks,
    },
    isNil,
  );
};

const constructSplitShipment = (data, baseModel) => {
  // parcels is the guide to iterate the shipment model
  const { parcels = [] } = data || {};

  return parcels.map((p) => {
    const extendedModel: any = constructParcelModel(p);

    return omitBy(
      {
        ...baseModel,
        _id: p._id,
        ...extendedModel,
      },
      isNil,
    );
  });
};

const model = (data: any) => {
  const orderFormType = data.orderType?.toUpperCase();

  const baseModel = {
    _id: data?._id,
    createdAt: data?.createdAt,
    deliveryType: data.deliveryType,
    hasInsurance: data.hasInsurance,
    looseOrders: data.looseOrders,
    orderType: orderFormType,
    receiverInfo: {
      fullName: data.recipientName,
      contactEmail: data.recipientEmail,
      contactNumber: {
        countryCode: data.recipientContact.dialCode,
        number: data.recipientContact.number,
        fullNumber:
          data.recipientContact.dialCode + data.recipientContact.number,
      },
      address: {
        // spread receiverInfo address if found, incase of missing 'coordinate' object
        ...data.receiverInfo?.address,
        ...data.recipientAddress,
      },
    },
    remarks: data.remarks,
    senderID: data.customer,
    senderInfo: {
      fullName: data.individualName,
      contactEmail: data.emailAddress,
      contactNumber: {
        countryCode: data.senderContact.dialCode,
        number: data.senderContact.number,
        fullNumber: data.senderContact.dialCode + data.senderContact.number,
        type: data.type || "MOBILE",
      },
      address: {
        ...data.senderAddress,
      },
    },
    senderLocationID: data.customerLocation,
    senderType: data.senderType?.toUpperCase(),
    serviceType: data.serviceType,
    specialCondition: data.specialConditions,
    specialCare: data.specialCare,
  };

  // if the orderType is individual or bulk, the following can be used
  if (SameOrderModel.includes(orderFormType)) {
    const { parcels = [] } = data || {};

    // there can only be one parcel, so take the first value
    const firstItem: any = first(parcels);
    const extendedModel: any = constructParcelModel(firstItem);

    const order = omitBy(
      {
        ...baseModel,
        ...extendedModel,
      },
      (val) => isNil(val) || val === "",
    );

    return { order };
  }

  if (orderFormType === ParcelTypeEnums.SPLIT) {
    const childrenOrders = constructSplitShipment(data, baseModel);

    // will this change?
    const { parcels = [] } = data || {};

    const firstItem: any = first(parcels);
    const extendedModel: any = constructParcelModel(firstItem);

    // im not sure why remarks is always appearing as empty string for split...
    const baseModelFix = !baseModel.remarks
      ? omit(baseModel, ["remarks"])
      : baseModel;

    const order = {
      ...baseModelFix,
      ...extendedModel,
      parcels: [],
    };

    return {
      order,
      childrenOrders,
    };
  }

  // will fail at this stage
  return baseModel;
};

export default model;
